import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'cutNumber',
  standalone: true,
})
export class CutNumberPipe implements PipeTransform {
  /**
   * Обрезает завершающие нули из дробной части
   */
  private trimTrailingZeros(fractionalPart: string): string {
    return fractionalPart.replace(/0+$/, '');
  }

  transform(value: number | string, digitsInfo: string = '1.2-2', locale: string = 'en-US'): string {
    if (value == null || value === '') {
      return '';
    }

    const num = Number(value);
    if (isNaN(num)) {
      return String(value);
    }

    // Если число равно 0, возвращаем 0
    if (num === 0) {
      const match = digitsInfo.match(/^(\d+)\.(\d+)-(\d+)$/);
      if (!match) {
        throw new Error(`Invalid digitsInfo format: ${digitsInfo}`);
      }
      const [, , minFractionStr] = match;
      const minFraction = parseInt(minFractionStr, 10);
      const zeros = '0'.repeat(minFraction);
      const withSeparators = new Intl.NumberFormat(locale).format(0);
      return zeros.length > 0 ? `${withSeparators}.${zeros}` : withSeparators;
    }

    const match = digitsInfo.match(/^(\d+)\.(\d+)-(\d+)$/);
    if (!match) {
      throw new Error(`Invalid digitsInfo format: ${digitsInfo}`);
    }

    const [, , minFractionStr, maxFractionStr] = match;
    const minFraction = parseInt(minFractionStr, 10);
    const maxFraction = parseInt(maxFractionStr, 10);

    // Преобразуем число в строку, используя toFixed для очень маленьких чисел
    // Это помогает обработать научную нотацию и очень маленькие числа
    let numStr: string;
    const originalStr = String(value);

    // Для scientific notation нормализуем строку через toFixed,
    // чтобы сохранить текущую логику отображения очень маленьких значений.
    if (originalStr.includes('e') || originalStr.includes('E')) {
      // Используем toFixed с достаточным количеством знаков для точного представления
      numStr = num.toFixed(20);
    } else {
      numStr = originalStr;
    }

    const [intPart, fracPart = ''] = numStr.split('.');

    // Проверяем, если после обрезания до maxFraction получается 0
    let trimmedFrac = fracPart.substring(0, maxFraction);

    // Если после обрезания получается 0, но исходное число не 0, показываем первую значащую цифру
    // Проверяем, что все цифры в trimmedFrac - нули, и исходное число не равно 0
    const allZeros = trimmedFrac.length > 0 && trimmedFrac.split('').every(digit => digit === '0');
    const isIntPartZero = intPart === '0' || intPart === '-0';

    if (allZeros && Math.abs(num) > 0 && isIntPartZero) {
      // Находим первую ненулевую цифру в дробной части
      for (let i = 0; i < fracPart.length; i++) {
        if (fracPart[i] !== '0') {
          let significantFrac = fracPart.substring(0, i + 1);
          // Обрезаем завершающие нули
          significantFrac = this.trimTrailingZeros(significantFrac);
          // Используем абсолютное значение для форматирования целой части
          const absIntPart = Math.abs(Number(intPart));
          const withSeparators = new Intl.NumberFormat(locale).format(absIntPart);
          const sign = num < 0 ? '-' : '';
          // Если после обрезания нулей дробная часть пустая, возвращаем только целую часть
          if (significantFrac.length === 0) {
            return `${sign}${withSeparators}`;
          }
          return `${sign}${withSeparators}.${significantFrac}`;
        }
      }
    }

    // Обычное форматирование
    // Обрезаем завершающие нули из дробной части
    trimmedFrac = this.trimTrailingZeros(trimmedFrac);

    // Добавляем нули до minFraction, если нужно (для соблюдения минимального количества знаков)
    while (trimmedFrac.length < minFraction) {
      trimmedFrac += '0';
    }

    // Если после всех операций дробная часть пустая, возвращаем только целую часть
    if (trimmedFrac.length === 0) {
      const withSeparators = new Intl.NumberFormat(locale).format(Number(intPart));
      return withSeparators;
    }

    const formatted = `${intPart}.${trimmedFrac}`;
    const parts = formatted.split('.');
    const withSeparators = new Intl.NumberFormat(locale).format(Number(parts[0]));

    return `${withSeparators}.${parts[1]}`;
  }
}

